import React, { useState, useEffect } from 'react';
import axiosConfig from '../../api/axiosConfig';
import useStudentAuth from '../../hooks/useStudentAuth';

export default function AllPaymentsPage() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalPayment: 0,
    paymentCount: 0,
    lastPaymentDate: null
  });
  const { user } = useStudentAuth();

  useEffect(() => {
    if (user?.id) {
      fetchPayments();
    }
  }, [user]);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const response = await axiosConfig.get(`/admin/odemes/musteri/${user.id}`);
      const paymentsData = response.data || [];
      
      // İstatistikleri hesapla
      const totalPayment = paymentsData.reduce((sum, payment) => sum + parseFloat(payment.tutar || 0), 0);
      const lastPayment = paymentsData.length > 0 ? paymentsData[0] : null;
      
      setPayments(paymentsData);
      setStats({
        totalPayment,
        paymentCount: paymentsData.length,
        lastPaymentDate: lastPayment?.tarih
      });
    } catch (error) {
      console.error('Ödemeler yüklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('tr-TR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return '-';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('tr-TR', { 
      style: 'currency', 
      currency: 'TRY',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(Math.abs(amount || 0));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Ödemeler yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <h1 className="text-3xl font-bold mb-8">Ödeme Geçmişim</h1>
      
      {/* Özet Kartlar */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="bg-gradient-to-r from-green-500 to-green-600 text-white p-6 rounded-xl shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-90">Toplam Ödeme</p>
              <p className="text-2xl font-bold">{formatCurrency(stats.totalPayment)}</p>
            </div>
            <span className="text-3xl opacity-80">💰</span>
          </div>
        </div>
        
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-6 rounded-xl shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-90">Ödeme Sayısı</p>
              <p className="text-2xl font-bold">{stats.paymentCount}</p>
            </div>
            <span className="text-3xl opacity-80">📊</span>
          </div>
        </div>
        
        <div className="bg-gradient-to-r from-purple-500 to-purple-600 text-white p-6 rounded-xl shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-90">Son Ödeme</p>
              <p className="text-lg font-bold">{formatDate(stats.lastPaymentDate)}</p>
            </div>
            <span className="text-3xl opacity-80">📅</span>
          </div>
        </div>
      </div>

      {/* Ödeme Tablosu */}
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="px-6 py-4 bg-gradient-to-r from-green-600 to-emerald-600">
          <h2 className="text-xl font-semibold text-white">Ödeme Geçmişi</h2>
        </div>
        
        {payments.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-gray-500 text-lg">Henüz ödeme kaydınız bulunmamaktadır.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Tarih
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Banka
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Tutar
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {payments.map((payment) => (
                  <tr key={payment.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {formatDate(payment.tarih)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {payment.banka_ismi || 'Nakit'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <span className="inline-flex px-3 py-1 text-sm font-semibold rounded-full bg-green-100 text-green-800">
                        {formatCurrency(payment.tutar)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50">
                <tr>
                  <td colSpan="2" className="px-6 py-4 text-right font-semibold">
                    TOPLAM:
                  </td>
                  <td className="px-6 py-4 text-right">
                    <span className="text-lg font-bold text-green-600">
                      {formatCurrency(stats.totalPayment)}
                    </span>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {/* Bilgi Notu */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="text-sm text-blue-800">
            <strong>Bilgi:</strong> Bu listede yaptığınız tüm ödemeler gösterilmektedir. Herhangi bir sorunuz varsa lütfen yönetimle iletişime geçiniz.
          </div>
        </div>
      </div>
    </div>
  );
}
